import { Card, EyebrowLabel } from '@thegpvc/ui';
import { Clock } from 'lucide-react';
import { Pulse } from './Pulse';
import { buildPulse, hasPulse, formatGap, pulseScale, sittingActivityBounds } from '../utils/pulse';
import { text, border, cx } from '../styles/theme';
import { formatDate, formatDuration, formatNumber } from '../utils/format';
import { FlaggedInsights } from './FlaggedInsights';
import { toFlaggedInsights } from '../utils/flaggedInsights';
import { modeLabel, modeChipClass } from '../utils/mode';
import { sumTotalTokens } from '../utils/tokenLabels';
import type { Sitting, CoachingInsight, FrictionMoment } from '../types/api';

/**
 * The chapter feed (#489 Phase 2c) — a session read as an ordered sequence of
 * sitting-chapters. Each chapter leads with a short title (its headline),
 * followed by its executive summary, a mini-Pulse, and a subdued vitals row.
 * Chapters after the first are introduced by a resumption seam (the idle gap or
 * context reset that started them).
 *
 * Single-sitting sessions (the common case) have no chapter structure worth
 * showing — the hero Pulse already tells the whole story — so this renders
 * nothing for them. Chapters degrade gracefully to Pulse + vitals until the
 * sitting is summarized.
 */

const RESET_LABELS: Record<string, string> = {
  clear: 'Resumed after /clear',
  compaction: 'Resumed after compaction',
};

const isUnset = (ts: string | undefined): boolean => !ts || parseInt(ts, 10) <= 0;

function seamLabel(sitting: Sitting, gapMs: number | null): string {
  if (sitting.breakReason === 'idle_gap') {
    return gapMs != null && gapMs > 0 ? `Resumed after a ${formatGap(gapMs)} gap` : 'Resumed after a gap';
  }
  return RESET_LABELS[sitting.breakReason] ?? sitting.breakReason;
}

/** A resumption seam drawn clearly between two chapters. */
function Seam({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 my-6" aria-label={label}>
      <span className={cx('flex-1 border-t', border.default)} />
      <span className={cx('inline-flex items-center gap-1.5 text-xs uppercase tracking-wide', text.muted)}>
        <Clock className="w-3.5 h-3.5" />
        {label}
      </span>
      <span className={cx('flex-1 border-t', border.default)} />
    </div>
  );
}

function Chapter({
  sitting,
  index,
  scaleMax,
  insights,
  friction,
}: {
  sitting: Sitting;
  index: number;
  scaleMax: number;
  insights?: CoachingInsight[];
  friction?: FrictionMoment[];
}) {
  const title = sitting.title?.trim();
  const exec = sitting.executiveSummary?.trim();
  const skipped = sitting.summaryStatus === 'skip' && !exec;

  const isOngoing = isUnset(sitting.endedAt);
  const durationMs =
    !isOngoing && !isUnset(sitting.startedAt)
      ? (parseInt(sitting.endedAt, 10) - parseInt(sitting.startedAt, 10)) * 1000
      : null;
  const tokens = sumTotalTokens(sitting);
  // Normalize each chapter mini to the session-wide scale so a quiet chapter
  // renders short next to a heavy one (honest comparison, per the Pulse spec).
  const chapterPulse = buildPulse([sitting], scaleMax);
  // A pulse strip only communicates something with enough activity; below the
  // threshold it reads as a broken near-empty rule, so fall back to the numbers.
  const events = sitting.userTurns + sitting.aiTurns + sitting.toolCalls;
  const showStrip = hasPulse(chapterPulse) && events >= 10;

  const mode = modeLabel(sitting.mode);

  return (
    <div>
      <span className={cx('text-xs font-medium uppercase tracking-wide', text.muted)}>Chapter {index + 1}</span>
      {mode && (
        <span className={cx('ml-2 rounded-full border px-1.5 py-0.5 text-[10px] font-medium align-middle', modeChipClass(sitting.mode))}>
          {mode}
        </span>
      )}

      {/* Headline: the title when there is one. When there's no title, the exec
          summary is the chapter's content and renders as a normal-weight
          description below — never promoted to a bold headline. */}
      {title ? (
        <h4 className={cx('mt-0.5 text-base font-semibold leading-snug', text.primary)}>{title}</h4>
      ) : !exec ? (
        <h4 className={cx('mt-0.5 text-sm italic font-normal', text.muted)}>
          {skipped ? 'Too little activity to summarize.' : 'Not yet summarized.'}
        </h4>
      ) : null}

      {/* Description: the executive summary, whether it sits under a title or
          stands alone as the untitled chapter's summary. */}
      {exec && (
        <p className={cx('mt-1 text-sm leading-relaxed', text.secondary)}>{exec}</p>
      )}

      {showStrip && (
        <div className="mt-3 overflow-x-auto">
          <Pulse data={chapterPulse} variant="full" height={36} ariaLabel={`Chapter ${index + 1} activity pulse`} />
        </div>
      )}

      {/* "Dixie flagged" — the AI-generated findings grounded in this chapter,
          rendered as the nested cream panel (#601). Sits after the activity
          strip and before the mono meta footer. Returns null when the chapter
          has no findings. Fed for now by the chapter's coaching + friction
          signals via the toFlaggedInsights bridge. */}
      <div className="mt-3">
        <FlaggedInsights insights={toFlaggedInsights(insights, friction)} />
      </div>

      {/* All metadata in one subdued row, including the timestamp. */}
      <div className={cx('text-xs mt-2 tabular-nums', text.muted)}>
        {formatDate(sitting.startedAt)}
        {durationMs != null && <> · {formatDuration(durationMs)}</>}
        {' · '}{sitting.userTurns} prompts · {sitting.aiTurns} AI turns · {sitting.toolCalls} tool calls
        {tokens > 0 && <> · {formatNumber(tokens)} tokens</>}
      </div>
    </div>
  );
}

export function SessionChapters({
  sittings,
  insights,
  friction,
}: {
  sittings: Sitting[];
  insights?: CoachingInsight[];
  friction?: FrictionMoment[];
}) {
  if (!sittings || sittings.length <= 1) return null;

  const ordered = [...sittings].sort((a, b) => a.ordinal - b.ordinal);
  const scaleMax = pulseScale(ordered);

  // Index chapter-grounded insights by their sitting ordinal for link-through.
  const insightsByOrdinal = new Map<number, CoachingInsight[]>();
  for (const it of insights ?? []) {
    if (typeof it.sittingOrdinal !== 'number') continue;
    const arr = insightsByOrdinal.get(it.sittingOrdinal) ?? [];
    arr.push(it);
    insightsByOrdinal.set(it.sittingOrdinal, arr);
  }
  // Index friction Moments by the chapter they occurred in.
  const frictionByOrdinal = new Map<number, FrictionMoment[]>();
  for (const f of friction ?? []) {
    if (typeof f.sittingOrdinal !== 'number') continue;
    const arr = frictionByOrdinal.get(f.sittingOrdinal) ?? [];
    arr.push(f);
    frictionByOrdinal.set(f.sittingOrdinal, arr);
  }

  return (
    <div id="chapters" className="scroll-mt-16 space-y-4">
      <EyebrowLabel dot color="orange">Chapters ({ordered.length})</EyebrowLabel>
      <ol className="space-y-4">
        {ordered.map((sitting, idx) => {
          const prev = idx > 0 ? ordered[idx - 1] : null;
          // Honest gap: from the previous chapter's last *activity* to this
          // one's first activity, so trailing/leading idle rolls into the gap
          // instead of reading as "0h" when zero-token entries tail the sitting.
          // Falls back to the sitting-boundary gap when no pulse series exists.
          const prevBounds = prev ? sittingActivityBounds(prev) : null;
          const thisBounds = sittingActivityBounds(sitting);
          const gapMs =
            prevBounds?.lastMs != null && thisBounds?.firstMs != null
              ? thisBounds.firstMs - prevBounds.lastMs
              : prev && !isUnset(prev.endedAt) && !isUnset(sitting.startedAt)
                ? (parseInt(sitting.startedAt, 10) - parseInt(prev.endedAt, 10)) * 1000
                : null;
          return (
            <li key={sitting.ordinal} id={`sitting-${sitting.ordinal}`} className="scroll-mt-16">
              {idx > 0 && <Seam label={seamLabel(sitting, gapMs)} />}
              {/* Each chapter is its own card. */}
              <Card radius="card">
                <Card.Body>
                  <Chapter
                    sitting={sitting}
                    index={idx}
                    scaleMax={scaleMax}
                    insights={insightsByOrdinal.get(sitting.ordinal)}
                    friction={frictionByOrdinal.get(sitting.ordinal)}
                  />
                </Card.Body>
              </Card>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
